import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBox,
  faWeightHanging,
  faRulerCombined,
  faCoins,
  faShieldAlt,
  faPencilAlt,
  faFileAlt,
} from '@fortawesome/free-solid-svg-icons';
import modalStyles from '../../styles/common/Modal.module.css';
import styles from '../../styles/common/EditPackageDataModal.module.css';

export interface PackageItemData {
  length: string | number;
  width: string | number;
  height: string | number;
  weight: string | number;
  fob?: string | number;
  packageContent?: string;
  content?: string;
  isFragile?: boolean;
}

interface PackageDetailsModalProps {
  show: boolean;
  onHide: () => void;
  packages: PackageItemData[];
  unitSystem?: "metric" | "imperial";
  currency?: string;
  parcelOrdDoc?: string;
}

const PackageDetailsModal: React.FC<PackageDetailsModalProps> = ({
  show,
  onHide,
  packages = [],
  unitSystem = "imperial",
  currency = "USD",
  parcelOrdDoc = "PARCEL",
}) => {
  const isMetric = unitSystem === "metric";
  const sizeUnit = isMetric ? "cm" : "in";
  const weightUnit = isMetric ? "kg" : "lb";

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className={styles.editPackageDataModal}
      contentClassName={styles.scaleIn}
      animation={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className={`${styles.modalTitle} ${modalStyles.modalTitle}`}>
          <FontAwesomeIcon icon={faBox} /> Detalle del Paquete ({packages.length} {packages.length === 1 ? 'Pieza' : 'Piezas'})
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className={styles.editPackageDataModalBody} style={{ padding: '24px', maxHeight: '70vh', overflowY: 'auto' }}>
        {packages && packages.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {packages.map((pkg, idx) => (
              <div
                key={idx}
                style={{
                  border: '1px solid #cbd5e1',
                  borderRadius: '12px',
                  padding: '16px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px' }}>
                  <span style={{ fontWeight: 700, fontSize: '14px', color: '#000000', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FontAwesomeIcon icon={faBox} style={{ fontSize: '12px', color: '#64748b' }} />
                    Pieza #{idx + 1}
                  </span>
                  {parcelOrdDoc === "PARCEL" && (
                    <span
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: '3px 8px',
                        borderRadius: '6px',
                        backgroundColor: pkg.isFragile ? '#fee2e2' : '#f1f5f9',
                        color: pkg.isFragile ? '#dc2626' : '#64748b',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <FontAwesomeIcon icon={faShieldAlt} />
                      {pkg.isFragile ? "Frágil: SÍ" : "Frágil: NO"}
                    </span>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
                  <div style={{ backgroundColor: '#f8fafc', padding: '10px 12px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                      <FontAwesomeIcon icon={faRulerCombined} style={{ marginRight: '4px' }} />
                      Dimensiones
                    </span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                      {pkg.length || 0} × {pkg.width || 0} × {pkg.height || 0} {sizeUnit}
                    </span>
                  </div>

                  <div style={{ backgroundColor: '#f8fafc', padding: '10px 12px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                      <FontAwesomeIcon icon={faWeightHanging} style={{ marginRight: '4px' }} />
                      Peso
                    </span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                      {pkg.weight || 0} {weightUnit}
                    </span>
                  </div>

                  <div style={{ backgroundColor: '#f8fafc', padding: '10px 12px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                      <FontAwesomeIcon icon={faCoins} style={{ marginRight: '4px' }} />
                      Valor FOB
                    </span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                      ${pkg.fob || 0} {currency}
                    </span>
                  </div>
                </div>

                {(pkg.packageContent || pkg.content) && (
                  <div style={{ marginTop: '12px', backgroundColor: '#f8fafc', padding: '10px 12px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                      <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: '4px' }} />
                      Descripción del Contenido
                    </span>
                    <span style={{ fontSize: '13px', fontWeight: 500, color: '#334155', wordBreak: 'break-word' }}>
                      {pkg.packageContent || pkg.content}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p style={{ textAlign: 'center', color: '#64748b', margin: 0 }}>No hay información de paquetes disponible.</p>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          className={modalStyles.closeBtn}
          onClick={onHide}
        >
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PackageDetailsModal;
